import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { translate, translateFormat } from '../Services/translation';
import { getPackSize } from '../Actions/ProductConfigurator.action';
import { stockTone } from './ProductStockStatus';
import { formatPrice } from '../Services/formatPrice';

const statusCodes = [
    'RedMax',
    'RedHighest',
    'RedHigh',
    'RedMed',
    'RedLow',
    'Yellow',
    'Green'
];

const getSelected = (article) => article.configVariants.filter(x => x.sku == article.defaultVariant)[0];

const getTotal = (articles = [], field = 'price') => articles.reduce((total, article) => {
    const variant = getSelected(article);
    return variant ? total + variant[field] * article.defaultAmmount : total;
}, 0);

const getSlowestVariant = (articles = []) => {
    const selected = articles.map(getSelected).filter(x => x && x.deliveryStatus != null);
    for (const code of statusCodes) {
        const variant = selected.find(x => x.deliveryStatus.startsWith(code));
        if (variant) {
            return variant;
        }
    }
    return null;
}

const isDisabled = (articles = []) => articles.some(article => {
    const selected = getSelected(article);
    return selected != undefined && selected.soldOut;
});

const Price = ({ price, listPrice }) => listPrice > price
    ? <>
        <span className="product__campaign-price">{formatPrice(price)}</span>
        <span className="product__old-price">{formatPrice(listPrice)}</span>
    </>
    : <span className="price">{formatPrice(price)}</span>;

const DeliveryStatus = ({ variant }) => variant && variant.deliveryText
    ? <span className={`product-stock-status__row product-stock-status__row--${stockTone(variant.deliveryStatus)}`}>
        <span className="product-stock-status__dot" aria-hidden="true" />
        <span className="product-stock-status__text">{variant.deliveryText}</span>
    </span>
    : null;

const VariantSummary = ({ variant }) => {
    const packSize = Math.max(1, variant.packSize || 1);
    return <>
        <img className="pdp-config__image" src={variant.defaultImageUrl} alt="" />
        <span className="pdp-config__text">
            <span className="pdp-config__name">{variant.name}</span>
            <span className="pdp-config__unit-price"><Price price={variant.price} listPrice={variant.listPrice} /></span>
            {packSize > 1 && <span className="product-configurator__pack-size">{translateFormat('product.soldinpack', packSize)}</span>}
            <DeliveryStatus variant={variant} />
        </span>
    </>;
}

// The server renders the base product's price; once the sections load, the live total takes its place.
class LiveTotal extends React.Component {
    constructor(props) {
        super(props);
        this.node = document.createElement('span');
        this.node.className = 'pdp-config__live-total';
    }

    componentDidMount() {
        const host = document.getElementById(this.props.hostId);
        if (host) {
            host.textContent = '';
            host.appendChild(this.node);
        }
    }

    render() {
        return ReactDOM.createPortal(this.props.children, this.node);
    }
}

const Section = ({ article, increment, decrement, changeVariant }) => {
    const [open, setOpen] = useState(false);
    const pickerRef = useRef(null);
    const variant = getSelected(article) || article.configVariants[0];
    const hasVariants = article.configVariants.length > 1;

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onPointerDown = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const pick = (sku) => {
        setOpen(false);
        if (sku != article.defaultVariant) {
            changeVariant(article.defaultVariant, sku);
        }
    };

    return <section className="pdp-config__section">
        <h2 className="pdp-config__title">{article.name}</h2>
        <div className="pdp-config__picker" ref={pickerRef}>
            {hasVariants
                ? <button type="button" className="pdp-config__selected" aria-expanded={open} onClick={() => setOpen(!open)}>
                    <VariantSummary variant={variant} />
                    <span className="pdp-config__chevron" aria-hidden="true" />
                </button>
                : <div className="pdp-config__selected"><VariantSummary variant={variant} /></div>}
            {open && <ul className="pdp-config__options">
                {article.configVariants.map(option => <li key={option.sku}>
                    <button type="button"
                        className={`pdp-config__option${option.sku == article.defaultVariant ? ' pdp-config__option--selected' : ''}${option.soldOut ? ' pdp-config__option--sold-out' : ''}`}
                        aria-pressed={option.sku == article.defaultVariant}
                        onClick={() => pick(option.sku)}>
                        <img className="pdp-config__option-image" src={option.defaultImageUrl} alt="" />
                        <span className="pdp-config__option-text">
                            <span className="pdp-config__option-name">{option.name}</span>
                            <DeliveryStatus variant={option} />
                        </span>
                        <span className="pdp-config__option-price"><Price price={option.price} listPrice={option.listPrice} /></span>
                    </button>
                </li>)}
            </ul>}
        </div>
        <div className="pdp-config__quantity-row">
            <div className="product-detail__quantity-wrapper pdp-config__quantity">
                <button type="button" className="product-detail__quantity-decrease" disabled={article.defaultAmmount <= 0} onClick={() => decrement(article.defaultVariant)}>-</button>
                <span className="pdp-config__quantity-value">{article.defaultAmmount}</span>
                <button type="button" className="product-detail__quantity-increase" onClick={() => increment(article.defaultVariant)}>+</button>
            </div>
            <div className="pdp-config__line">
                <span className="pdp-config__line-calc">{article.defaultAmmount} × {formatPrice(variant.price)}</span>
                <span className="pdp-config__line-total">{formatPrice(variant.price * article.defaultAmmount)}</span>
            </div>
        </div>
    </section>;
}

const ProductConfigurator = ({ articles = [], emptyArticles = [], increment, decrement, labelEmpty, changeVariant, children }) => {
    if (articles.length === 0 && emptyArticles.length === 0) {
        return null;
    }

    const total = <Price price={getTotal(articles)} listPrice={getTotal(articles, 'listPrice')} />;

    return <div className="pdp-config">
        {articles.length > 0 && <LiveTotal hostId="ProductConfiguratorPrice">{total}</LiveTotal>}
        {articles.map((article, index) => <Section key={index} {...{ article, increment, decrement, changeVariant }} />)}
        {emptyArticles.map((article, index) => <section key={`empty-${index}`} className="pdp-config__section">
            <h2 className="pdp-config__title">{article.name}</h2>
            <p className="pdp-config__empty">{labelEmpty}</p>
        </section>)}
        {articles.length > 0 && <div className="pdp-config__total">
            <span className="pdp-config__total-label">{translate('checkout.cart.total')}</span>
            <span className="pdp-config__total-price">{total}</span>
        </div>}
        <div className={`pdp__buy pdp-config__buy${isDisabled(articles) ? ' pdp-config__buy--disabled' : ''}`} inert={isDisabled(articles) ? '' : undefined}>
            {children}
        </div>
        <DeliveryStatus variant={getSlowestVariant(articles)} />
    </div>;
}

export default ProductConfigurator;
